import {announce} from '@github-ui/aria-live'
import {getAnalyticsEvent} from '@github-ui/swp-core/lib/utils/analytics'
import {t} from '@github-ui/swp-core/lib/utils/localization'
import {CheckIcon, CopyIcon} from '@primer/octicons-react'
import {ActionMenu, Box, Button, Stack, Text} from '@primer/react-brand/esm'
import {useEffect, useEffectEvent, useState, useTransition} from 'react'
import {CopilotCliMobile} from './CopilotCliMobile'
import styles from './CopilotCliPicker.module.css'

export interface CopilotCliCommand {
  title: string
  command: string
}

interface CopilotCliPickerProps {
  commands: CopilotCliCommand[]
}

export function CopilotCliPicker({commands}: CopilotCliPickerProps) {
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [hasCopied, setHasCopied] = useState(false)
  const [, startTransition] = useTransition()
  const selectedCommand = commands[selectedIndex] ?? commands[0]
  const resetCopiedState = useEffectEvent(() => {
    startTransition(() => setHasCopied(false))
  })

  useEffect(() => {
    if (!hasCopied) return

    const timer = setTimeout(() => {
      resetCopiedState()
    }, 2000)

    return () => clearTimeout(timer)
  }, [hasCopied])

  if (!selectedCommand) return null

  const selectedCommandText = selectedCommand.command

  async function handleCopy() {
    if (
      typeof navigator === 'undefined' ||
      !navigator.clipboard ||
      typeof navigator.clipboard.writeText !== 'function'
    ) {
      return
    }

    try {
      await navigator.clipboard.writeText(selectedCommandText)
      setHasCopied(true)
      announce(t('Copied to clipboard'))
    } catch {
      // Silently ignore clipboard errors to avoid showing a false success state.
    }
  }

  return (
    <Box data-testid="copilot-cli-picker" className={styles.cliPickerContainer}>
      <Stack direction="vertical" padding="none" gap="condensed" className={styles.cliInstallCta}>
        <Box className={styles.customInputContainer}>
          <Stack padding="none" direction={{narrow: 'vertical', regular: 'horizontal'}} gap="none" alignItems="center">
            <ActionMenu onSelect={value => setSelectedIndex(Number(value))} menuAlignment="start">
              <ActionMenu.Button className={styles.installMethodButton}>{selectedCommand.title}</ActionMenu.Button>
              <ActionMenu.Overlay aria-label={t('Select installation method')}>
                {commands.map((command, index) => (
                  <ActionMenu.Item
                    key={`${command.title}-${command.command}`}
                    value={String(index)}
                    selected={index === selectedIndex}
                    {...getAnalyticsEvent({
                      action: 'select_install_option',
                      tag: 'button',
                      location: 'hero',
                      context: 'cli_install_cta',
                    })}
                  >
                    {command.title}
                  </ActionMenu.Item>
                ))}
              </ActionMenu.Overlay>
            </ActionMenu>
            <Box borderWidth="thin" borderColor="default" padding={16} className={styles.customInputCodeContainer}>
              <Text as="span" font="monospace" size="100" className={styles.customInputCode}>
                {selectedCommandText}
              </Text>
            </Box>
            <Button
              trailingVisual={
                hasCopied ? (
                  <CheckIcon className={styles.customInputCopyIcon} />
                ) : (
                  <CopyIcon className={styles.customInputCopyIcon} />
                )
              }
              variant="primary"
              className={styles.customInputCopyButton}
              onClick={handleCopy}
              aria-label={t('Copy installation command')}
              {...getAnalyticsEvent({
                action: 'copy_command',
                tag: 'button',
                location: 'hero',
                context: 'cli_install_cta',
              })}
            >
              {t('Copy')}
            </Button>
          </Stack>
        </Box>
      </Stack>
      <CopilotCliMobile />
    </Box>
  )
}
